import { useState, useEffect } from 'react';
import { ServerStackIcon, DocumentTextIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ingestionService, RagStats } from '../../services/ingestionService';

export function RagStatsCard() {
  const [stats, setStats] = useState<RagStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ingestionService.getRagStats();
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load RAG stats:', err);
      setError('Não foi possível carregar estatísticas do RAG');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-2 text-red-600">
          <XCircleIcon className="h-5 w-5" />
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 shadow rounded-lg overflow-hidden">
      <div className="px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <ServerStackIcon className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Base de Conhecimento RAG</h3>
              <p className="text-sm text-gray-600">ChromaDB + RAPTOR</p>
            </div>
          </div>
          <button
            onClick={loadStats}
            className="text-emerald-600 hover:text-emerald-700 transition-colors"
            title="Atualizar estatísticas"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Estatísticas principais */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Total de chunks/embeddings */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Chunks/Embeddings</p>
                <p className="mt-1 text-3xl font-bold text-emerald-600">
                  {stats?.total_documents.toLocaleString('pt-BR') || 0}
                </p>
              </div>
              <DocumentTextIcon className="h-10 w-10 text-emerald-200" />
            </div>
          </div>

          {/* Última atualização */}
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Última Atualização</p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {stats?.last_updated
                    ? (() => {
                      const d = new Date(stats.last_updated)
                      return isNaN(d.getTime())
                        ? stats.last_updated
                        : d.toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                    })()
                    : 'Nunca processado'}
                </p>
              </div>
              <ClockIcon className="h-10 w-10 text-gray-200" />
            </div>
          </div>
        </div>

        {/* Collections */}
        {stats?.collections && stats.collections.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Collections:</h4>
            <div className="space-y-2">
              {stats.collections.map((collection) => (
                <div
                  key={collection.name}
                  className="bg-white rounded-lg p-3 shadow-sm flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
                    <span className="text-sm font-medium text-gray-900 truncate max-w-md" title={collection.name}>
                      {collection.name}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-emerald-600">
                    {collection.count.toLocaleString('pt-BR')} docs
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status vazio */}
        {stats?.total_documents === 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              ⚠️ Nenhum documento processado ainda. Faça upload de documentos e clique em "Processar" para popular a base de conhecimento.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
